"use client"
import { Box, Button, Collapse, Divider, Typography } from "@mui/material"
import Drawer from "@mui/material/Drawer"
import NextLink from "common/bit/NextLink"
import PreSaleTag from "common/PreSaleTag"
import { NestedNavigationLinksType } from "common/SiteLinks"
import { useGlobalContext } from "contexts/GlobalContext"
import Image from "next/image"
import React from "react"
import ButtonBaseBlank from "reusable/ButtonBaseBlank"
import styled, { css } from "styled-components"
import colors from "theme/colors"
import { HEADER_HEIGHT_M, StyledCol, TypographyRtl } from "theme/shared"
import HeaderTopButtons from "../HeaderTopButtons"
import SubFolderArrow from "./SubFolderArrow"

const StyledDrawer = styled(Drawer)`
  .MuiPaper-root {
    /* Full-viewport overlay (DLK-845). A top-anchored MUI Drawer paper is
       content-sized by default, so a drawer with little content collapsed to
       ~65% and the dimmed page showed through below. Force full height so the
       menu always covers the screen; dvh tracks the visible area on mobile,
       vh is the fallback. */
    box-sizing: border-box;
    height: 100vh;
    height: 100dvh;
    padding-top: ${HEADER_HEIGHT_M}px;
    padding-bottom: 32px;
    background-color: ${(p) => p.theme.colors.headerColor};
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.25);
  }
  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.4);
    height: 100vh;
  }
  z-index: 1150 !important;
`
const StyledDrawerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`
const StyledDivider = styled(Divider)`
  width: 100%;
  background-color: ${(p) => p.theme.colors.darkGrey};
  height: 0.5px;
`
const StyledLinkButton = styled(Button)<{
  $selected?: boolean
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  &:hover {
    p {
      text-decoration: none;
    }
    background: none;
    border: none;
  }
  padding: 10px 20px;
  justify-content: space-between;
`
const StyledTypographyRtl = styled(TypographyRtl)<{
  $underlined?: boolean
  $fontSize?: string
  $fontWeight?: string
}>`
  padding-right: 20px;
  ${(p) =>
    p.$underlined &&
    css`
      position: relative;
      width: fit-content;
      margin: 0 20px 14px;
      font-size: ${p.$fontSize || "16px"};
      font-weight: ${p.$fontWeight || "normal"};
      line-height: 19px;
      letter-spacing: 0.08em;
      text-align: center;
      vertical-align: middle;
      padding: 0;
      &::after {
        content: "";
        display: block;
        width: 100%;
        height: 2px;
        background-color: ${p.theme.colors.white};
        position: absolute;
        bottom: -4px;
        left: 0;
        border-radius: 200px;
      }
    `}
`
const StyledLinkBtn = styled(ButtonBaseBlank)<{ $selected?: boolean }>`
  padding: 14px 30px 14px 0px;
  display: flex !important;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  &::before {
    content: "•";
    display: inline-block;
    margin-left: 8px;
    font-size: 1.5em;
    color: ${(p) => p.theme.colors.borderGrey};
  }
`

const StyledTypography = styled(Typography)`
  font-size: 14px;
`

const StyledModelWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  width: 94px;
  /* overflow: hidden; */
  position: relative;
`
const StyledModel = styled(Box).attrs({
  // component: "ul"
})`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px 20px;
  justify-items: center;
  align-items: center;
  width: 100%;
  overflow: hidden;
  padding: 0 20px;
  margin: 10px 0 20px;
`
const StyledModelLi = styled(Box).attrs({
  // component: "li"
})`
  height: 100%;
`
const StyledImage = styled(Image)`
  flex-shrink: 0;
  width: 100%;
  /* transform: scale(1.3); */
  max-width: 130px !important;
  inset-inline-start: calc((94px - 130px) / 2) !important;
  object-fit: contain;
  aspect-ratio: 1 / 1;
  height: auto;
`

const StyledTagWrapper = styled.div`
  position: absolute;
  top: 74px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
`

type SideDrawerProps = {
  isOpen: boolean
  toggleDrawer: (
    open: boolean
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void
  // CMS menu (GET /api/menu) mapped to nav links — same source as the desktop
  // header (DLK-739). Falls back to the hardcoded SideDrawerMobile when absent.
  links?: NestedNavigationLinksType[]
}

// The "all models" entry (url path "/models") is rendered as the underlined
// link beneath the models grid, not as a plain nav row.
const ALL_MODELS_PATH = "/models"
const toPath = (href?: string) => {
  if (!href) return ""
  try {
    return new URL(href, "https://x").pathname.replace(/\/+$/, "")
  } catch {
    return href.replace(/\/+$/, "")
  }
}
const isAllModels = (item: NestedNavigationLinksType) =>
  item.links.length === 1 && toPath(item.links[0].link) === ALL_MODELS_PATH

const SideDrawer = ({ isOpen, toggleDrawer, links }: SideDrawerProps) => {
  const [open, setOpen] = React.useState<string | false>(false)
  // Get the modelList from GlobalContext for later use
  const { modelList } = useGlobalContext()

  const navItems = links

  const handleClick = (panel: string) => {
    setOpen(panel !== open ? panel : false)
  }

  React.useEffect(() => {
    if (isOpen) {
      return
    }
    setOpen(false)
  }, [isOpen])

  // Filter out "coming soon" models (carState === "Future")
  const availableModels = modelList?.filter(
    (model) => model.carState !== "Future"
  )

  return (
    <StyledDrawer anchor={"top"} open={isOpen} onClose={toggleDrawer(false)}>
      <StyledDrawerWrapper role="presentation">
        <StyledDivider />
        <HeaderTopButtons closeDrawer={toggleDrawer(false)} />
        {availableModels && availableModels?.length > 0 && (
          <StyledModel>
            {availableModels?.map((item, index) => (
              <StyledModelLi key={index}>
                <NextLink href={item.link} onClick={toggleDrawer(false)}>
                  <StyledModelWrap
                    style={{ gap: item.carState === "PreSale" ? "8px" : "6px" }}
                  >
                    <StyledImage
                      src={item.image?.imageUrl}
                      alt={item.image?.alt || ""}
                      width={120}
                      height={90}
                    />
                    {item.carState === "PreSale" && (
                      <StyledTagWrapper>
                        <PreSaleTag size="small" />
                      </StyledTagWrapper>
                    )}
                    <StyledTypography
                      align="center"
                      variant="body2"
                      color="secondary"
                    >
                      {item.name}
                    </StyledTypography>
                  </StyledModelWrap>
                </NextLink>
              </StyledModelLi>
            ))}
          </StyledModel>
        )}
        <StyledCol $gap="20px" $notCenter $fullWidth $mt="12px">
          {navItems?.map((item, index) => (
            <div key={index}>
              {item.links.length === 1 ? (
                <>
                  <NextLink
                    href={item.links[0].link}
                    openInNewTab={item.links[0].blank}
                  >
                    <StyledTypographyRtl
                      variant="body1"
                      onClick={toggleDrawer(false)}
                      $color={
                        item.title === open ? colors.white : colors.neutral300
                      }
                      $fontWeight={item.title === open ? "400" : "300"}
                      $underlined={isAllModels(item)}
                      $fontSize={isAllModels(item) ? "16px" : "18px"}
                    >
                      {item.title}
                    </StyledTypographyRtl>
                  </NextLink>
                </>
              ) : (
                <>
                  <StyledLinkButton
                    onClick={() => {
                      handleClick(item.title)
                    }}
                    $selected={item.title === open}
                    disableFocusRipple={false}
                    disableRipple={false}
                    aria-expanded={item.title === open ? true : false}
                  >
                    <TypographyRtl
                      variant="body1"
                      $color={
                        item.title === open ? colors.white : colors.neutral300
                      }
                      $fontWeight={item.title === open ? "400" : "300"}
                      $fontSize={isAllModels(item) ? "16px" : "18px"}
                    >
                      {item.title}
                    </TypographyRtl>
                    <SubFolderArrow open={item.title === open} />
                  </StyledLinkButton>
                  <Collapse
                    in={item.title === open}
                    timeout="auto"
                    unmountOnExit
                  >
                    {item.links.map((item, index) => (
                      <NextLink href={item.link} key={index}>
                        <StyledLinkBtn onClick={toggleDrawer(false)}>
                          <TypographyRtl
                            variant="body2"
                            $color={colors.borderGrey}
                          >
                            {item.title}
                          </TypographyRtl>
                        </StyledLinkBtn>
                      </NextLink>
                    ))}
                  </Collapse>
                </>
              )}
            </div>
          ))}

          {/* Models section will be added here later */}
          {/* The modelList data is already available from GlobalContext */}
        </StyledCol>
      </StyledDrawerWrapper>
    </StyledDrawer>
  )
}

export default SideDrawer
